import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import {
  hasConsent,
  acceptAll,
  acceptNecessaryOnly,
  saveCustomConsent,
  getConsent,
} from '@/lib/consent';
import { initTracking } from '@/lib/tracking';

// Global function to reopen consent banner from footer
let reopenBanner: (() => void) | null = null;
export const openCookieSettings = () => reopenBanner?.();

/* ------------------------------------------------------------------ */
/*  Service detail data                                                */
/* ------------------------------------------------------------------ */

interface ServiceDetail {
  id: string;
  name: string;
  provider: { de: string; en: string };
  purpose: { de: string; en: string };
  dataCollected: { de: string[]; en: string[] };
  storageDuration: { de: string; en: string };
  privacyUrl: string;
}

const statisticsServices: ServiceDetail[] = [
  {
    id: 'ga4',
    name: 'Google Analytics 4',
    provider: { de: 'Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Irland', en: 'Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Ireland' },
    purpose: {
      de: 'Wir verwenden Google Analytics 4, einen Web-Analysedienst. Dieser sammelt Nutzungsdaten unserer Website und ermöglicht es, Reports zusammenzustellen und damit die Nutzung der Website zu verbessern. Die Anonymisierung der IP-Adressen findet statt, sodass die anonymisierte IP-Adresse keiner Person mehr zuordenbar ist, bevor sie an Google weitergeleitet wird.',
      en: 'We use Google Analytics 4, a web analytics service. It collects usage data from our website and enables us to compile reports and improve the use of the website. IP addresses are anonymized so that the anonymized IP address can no longer be attributed to any person before being forwarded to Google.',
    },
    dataCollected: {
      de: ['Anonymisierte IP-Adresse', 'Seitenaufrufe und Verweildauer', 'Geräte- und Browserinformationen', 'Referrer-URL', 'Ungefährer Standort (Land/Stadt)'],
      en: ['Anonymized IP address', 'Page views and session duration', 'Device and browser information', 'Referrer URL', 'Approximate location (country/city)'],
    },
    storageDuration: { de: 'Bis zu 14 Monate', en: 'Up to 14 months' },
    privacyUrl: 'https://policies.google.com/privacy',
  },
];

const marketingServices: ServiceDetail[] = [
  {
    id: 'meta-pixel',
    name: 'Meta Pixel (Facebook)',
    provider: { de: 'Meta Platforms Ireland Limited, 4 Grand Canal Square, Grand Canal Harbour, Dublin 2, Irland', en: 'Meta Platforms Ireland Limited, 4 Grand Canal Square, Grand Canal Harbour, Dublin 2, Ireland' },
    purpose: {
      de: 'Das Meta Pixel ist ein Analyse-Tool, das die Effektivität von Werbeanzeigen misst, indem es die Handlungen erfasst, die Personen auf unserer Website vornehmen. Es ermöglicht uns, zielgerichtete Werbung auf Facebook und Instagram zu schalten und den Erfolg unserer Werbekampagnen zu messen.',
      en: 'The Meta Pixel is an analytics tool that measures the effectiveness of advertising by tracking the actions people take on our website. It enables us to deliver targeted advertising on Facebook and Instagram and measure the success of our advertising campaigns.',
    },
    dataCollected: {
      de: ['HTTP-Header-Informationen (IP-Adresse, Browser, Standortdaten)', 'Pixel-spezifische Daten (Pixel-ID, Facebook-Cookie)', 'Button-Klick-Daten', 'Seitenaufrufe und Conversion-Events'],
      en: ['HTTP header information (IP address, browser, location data)', 'Pixel-specific data (pixel ID, Facebook cookie)', 'Button click data', 'Page views and conversion events'],
    },
    storageDuration: { de: 'Bis zu 180 Tage', en: 'Up to 180 days' },
    privacyUrl: 'https://www.facebook.com/privacy/policy/',
  },
  {
    id: 'google-ads',
    name: 'Google Ads Conversion Tracking',
    provider: { de: 'Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Irland', en: 'Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Ireland' },
    purpose: {
      de: 'Google Ads Conversion Tracking wird verwendet, um den Erfolg unserer Werbekampagnen bei Google zu messen. Es erfasst, ob ein Nutzer nach dem Klick auf eine Google-Anzeige eine gewünschte Aktion (Conversion) auf unserer Website ausführt, z. B. eine Registrierung für die Warteliste.',
      en: 'Google Ads Conversion Tracking is used to measure the success of our advertising campaigns on Google. It tracks whether a user performs a desired action (conversion) on our website after clicking on a Google ad, e.g. registering for the waitlist.',
    },
    dataCollected: {
      de: ['Anonymisierte IP-Adresse', 'Conversion-Daten (z. B. Registrierung)', 'Geräte- und Browserinformationen', 'Klickdaten der Anzeige'],
      en: ['Anonymized IP address', 'Conversion data (e.g. registration)', 'Device and browser information', 'Ad click data'],
    },
    storageDuration: { de: 'Bis zu 90 Tage', en: 'Up to 90 days' },
    privacyUrl: 'https://policies.google.com/privacy',
  },
  {
    id: 'affiliate',
    name: 'Empfehlungs-Zuordnung (Affiliate)',
    provider: { de: 'Cosmic Gap LDA (H2HUMANS), Lissabon, Portugal', en: 'Cosmic Gap LDA (H2HUMANS), Lisbon, Portugal' },
    purpose: {
      de: 'Wenn du über den Link oder Code einer Partnerin zu uns kommst, merken wir uns 30 Tage lang, wer dich empfohlen hat. So können wir der Partnerin ihre Provision zuordnen. Ohne deine Einwilligung gilt die Zuordnung nur für deinen aktuellen Besuch und wird danach gelöscht.',
      en: 'If you come to us via a partner\'s link or code, we remember for 30 days who recommended you. This lets us credit the partner with her commission. Without your consent, the attribution only applies to your current visit and is deleted afterwards.',
    },
    dataCollected: {
      de: ['Name der empfehlenden Partnerin', 'Art der Zuordnung (Link oder Code)', 'Zeitpunkt des Aufrufs'],
      en: ['Name of the recommending partner', 'Type of attribution (link or code)', 'Time of the visit'],
    },
    storageDuration: { de: '30 Tage', en: '30 days' },
    privacyUrl: '/datenschutz',
  },
];

/* ------------------------------------------------------------------ */
/*  Expandable service row                                             */
/* ------------------------------------------------------------------ */

const ServiceRow: React.FC<{
  service: ServiceDetail;
  de: boolean;
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
}> = ({ service, de, checked, onCheckedChange }) => {
  const [open, setOpen] = useState(false);
  const lang = de ? 'de' : 'en';

  return (
    <div className="border-b border-border/50 last:border-b-0">
      <div className="flex items-center justify-between py-2.5 px-1">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="flex items-center gap-1 text-left hover:bg-muted/30 transition-colors flex-1"
        >
          <span className="text-sm font-medium text-foreground">{service.name}</span>
          {open ? <ChevronUp className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />}
        </button>
        <Switch checked={checked} onCheckedChange={onCheckedChange} className="shrink-0 ml-3" />
      </div>
      {open && (
        <div className="px-1 pb-3 text-xs text-muted-foreground space-y-2.5">
          <div>
            <p className="font-medium text-foreground/80 mb-0.5">{de ? 'Anbieter' : 'Provider'}</p>
            <p>{service.provider[lang]}</p>
          </div>
          <div>
            <p className="font-medium text-foreground/80 mb-0.5">{de ? 'Beschreibung des Services' : 'Service Description'}</p>
            <p>{service.purpose[lang]}</p>
          </div>
          <div>
            <p className="font-medium text-foreground/80 mb-0.5">{de ? 'Erhobene Daten' : 'Data Collected'}</p>
            <ul className="list-disc pl-4 space-y-0.5">
              {service.dataCollected[lang].map((item, i) => <li key={i}>{item}</li>)}
            </ul>
          </div>
          <div>
            <p className="font-medium text-foreground/80 mb-0.5">{de ? 'Speicherdauer' : 'Storage Duration'}</p>
            <p>{service.storageDuration[lang]}</p>
          </div>
          <a
            href={service.privacyUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-primary hover:underline"
          >
            {de ? 'Datenschutzerklärung des Anbieters' : 'Provider Privacy Policy'}
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Main banner component                                              */
/* ------------------------------------------------------------------ */

export const CookieConsentBanner: React.FC = () => {
  const { language, setLanguage } = useLanguage();
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [statistics, setStatistics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  // Per-service state
  const [ga4, setGa4] = useState(false);
  const [metaPixel, setMetaPixel] = useState(false);
  const [googleAds, setGoogleAds] = useState(false);
  const [affiliate, setAffiliate] = useState(false);

  useEffect(() => {
    reopenBanner = () => {
      const existing = getConsent();
      if (existing) {
        setStatistics(existing.statistics);
        setMarketing(existing.marketing);
        setGa4(existing.services?.ga4 ?? existing.statistics);
        setMetaPixel(existing.services?.metaPixel ?? existing.marketing);
        setGoogleAds(existing.services?.googleAds ?? existing.marketing);
        setAffiliate(existing.services?.affiliate ?? existing.marketing);
      }
      setShowDetails(true);
      setVisible(true);
    };
    if (!hasConsent()) {
      setVisible(true);
    } else {
      initTracking();
    }
    return () => { reopenBanner = null; };
  }, []);

  if (!visible) return null;

  const handleAcceptAll = () => {
    acceptAll();
    initTracking();
    setVisible(false);
  };

  const handleNecessaryOnly = () => {
    acceptNecessaryOnly();
    setVisible(false);
  };

  const handleSaveCustom = () => {
    // Derive category from individual services
    const statsOn = ga4;
    const mktOn = metaPixel || googleAds || affiliate;
    saveCustomConsent(statsOn, mktOn, { ga4, metaPixel, googleAds, affiliate });
    initTracking();
    setVisible(false);
  };

  const de = language === 'de';

  /* ---- Layer 1: Initial consent view ---- */
  if (!showDetails) {
    return (
      <div className="fixed inset-0 z-[9999] bg-black/30 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6">
        <div className="w-full md:max-w-xl bg-background border border-border rounded-t-2xl md:rounded-2xl shadow-elevated p-5 md:p-6 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-3">
            <p className="text-base font-semibold text-foreground">
              {de ? 'Einwilligung in die Datenverarbeitung' : 'Consent to Data Processing'}
            </p>
            <button
              onClick={() => setLanguage(de ? 'en' : 'de')}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors border border-border rounded-full px-2.5 py-1"
            >
              {de ? 'EN' : 'DE'}
            </button>
          </div>

          <div className="text-sm text-foreground/90 leading-relaxed space-y-3 mb-4">
            <p>
              {de
                ? 'Wir setzen auf unserer Website Cookies und Tracking-Technologien ein. Der Einsatz von sogenannten technisch notwendigen Cookies ist für die Nutzung unserer Website notwendig. Zusätzlich können Sie uns, mit einem Klick auf die Schaltfläche „Akzeptieren" für die folgenden Verarbeitungen Ihre Einwilligung geben:'
                : 'We use cookies and tracking technologies on our website. The use of technically necessary cookies is required for the operation of our website. Additionally, by clicking "Accept", you can give your consent for the following processing activities:'}
            </p>

            <ul className="list-disc pl-5 text-foreground/80 space-y-1.5 text-[13px]">
              <li>{de ? 'Speicherung von Informationen auf Ihrem Endgerät und dem Zugriff auf Informationen in Ihrem Endgerät' : 'Storage of information on your device and access to information on your device'}</li>
              <li>{de ? 'Analyse- und Marketingzwecken sowie das Ausspielen von personalisierter Werbung' : 'Analysis and marketing purposes as well as the delivery of personalized advertising'}</li>
              <li>{de ? 'Verarbeitung Ihrer personenbezogenen Daten für die geräteübergreifende Erstellung und Verarbeitung von individuellen Nutzungsprofilen auf dieser Website und durch unsere Partner' : 'Processing of your personal data for cross-device creation and processing of individual usage profiles on this website and by our partners'}</li>
              <li>{de ? 'Übermittlung Ihrer Daten in Länder außerhalb der Europäischen Union (EU), namentlich in die USA sowie in andere Drittländer. In diesen Drittländern ist das Datenschutzniveau nicht mit dem Datenschutzniveau in der Europäischen Union vergleichbar. Es besteht das Risiko, dass Ihre Daten durch Behörden, zu Überwachungs- sowie Kontrollzwecken, verarbeitet werden können.' : 'Transfer of your data to countries outside the European Union (EU), namely to the USA and other third countries. The level of data protection in these third countries is not comparable to the level of data protection in the European Union. There is a risk that your data may be processed by authorities for surveillance and monitoring purposes.'}</li>
            </ul>

            <p className="text-xs text-muted-foreground">
              {de
                ? 'Weitere Informationen, auch zur Datenverarbeitung durch Drittanbieter und zum jederzeit möglichen Widerruf Ihrer Einwilligung, finden Sie unter dem Punkt „Einstellungen" sowie in unserer Datenschutzerklärung. Die jeweiligen Verarbeitungen, die Sie gestatten wollen, können Sie in den „Einstellungen" auswählen und mit „Speichern" bestätigen.'
                : 'Further information, including about data processing by third parties and the possibility to revoke your consent at any time, can be found under "Settings" and in our privacy policy. You can select the processing activities you wish to allow in "Settings" and confirm with "Save".'}
            </p>
          </div>

          {/* Legal links */}
          <div className="flex gap-4 text-xs text-muted-foreground mb-4">
            <a href="/datenschutz" className="hover:text-foreground transition-colors">{de ? 'Datenschutzerklärung' : 'Privacy Policy'}</a>
            <a href="/impressum" className="hover:text-foreground transition-colors">{de ? 'Impressum' : 'Imprint'}</a>
          </div>

          {/* Buttons */}
          <div className="flex flex-col gap-2">
            <div className="flex flex-row gap-2">
              <Button
                size="lg"
                className="flex-1 bg-[hsl(var(--h2h-black))] text-white hover:opacity-90"
                onClick={handleNecessaryOnly}
              >
                {de ? 'Nur notwendige' : 'Necessary only'}
              </Button>
              <Button
                size="lg"
                className="flex-1 bg-[hsl(var(--h2h-black))] text-white hover:opacity-90"
                onClick={() => setShowDetails(true)}
              >
                {de ? 'Einstellungen' : 'Settings'}
              </Button>
            </div>
            <Button
              size="lg"
              className="w-full bg-[hsl(var(--h2h-black))] text-white hover:opacity-90"
              onClick={handleAcceptAll}
            >
              {de ? 'Alle akzeptieren' : 'Accept all'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  /* ---- Layer 2: Detailed settings view ---- */
  return (
    <div className="fixed inset-0 z-[9999] bg-black/30 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6">
      <div className="w-full md:max-w-xl bg-background border border-border rounded-t-2xl md:rounded-2xl shadow-elevated max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-5 md:p-6 pb-3 border-b border-border">
          <div className="flex items-center justify-between mb-2">
            <p className="text-base font-semibold text-foreground">
              {de ? 'Privatsphäre-Einstellungen' : 'Privacy Settings'}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setLanguage(de ? 'en' : 'de')}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors border border-border rounded-full px-2.5 py-1"
              >
                {de ? 'EN' : 'DE'}
              </button>
              <button
                type="button"
                onClick={() => setShowDetails(false)}
                className="text-muted-foreground hover:text-foreground transition-colors text-lg leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            {de
              ? 'Umfassende Informationen zu den Cookies finden Sie hier in den individuellen Einstellungen und in unserer Datenschutzerklärung. Sie können Ihre Einwilligung jederzeit widerrufen. Mit dem Klick auf die Schaltfläche „Akzeptieren" stimmen Sie sämtlichen Cookies zu. Durch einen Klick auf die Schaltfläche „Speichern" stimmen Sie den individuellen Einstellungen zu.'
              : 'Comprehensive information about cookies can be found here in the individual settings and in our privacy policy. You can revoke your consent at any time. By clicking "Accept" you agree to all cookies. By clicking "Save" you agree to the individual settings.'}
          </p>
          <div className="flex gap-4 text-xs text-muted-foreground mt-2">
            <a href="/datenschutz" className="hover:text-foreground transition-colors">{de ? 'Datenschutzerklärung' : 'Privacy Policy'}</a>
            <a href="/impressum" className="hover:text-foreground transition-colors">{de ? 'Impressum' : 'Imprint'}</a>
          </div>
        </div>

        {/* Scrollable categories */}
        <div className="flex-1 overflow-y-auto p-5 md:p-6 pt-4 space-y-4">

          {/* Necessary */}
          <div className="rounded-xl border border-border p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-foreground">{de ? 'Technisch notwendig' : 'Technically Necessary'}</span>
              <span className="text-xs text-muted-foreground italic">{de ? 'Immer aktiv' : 'Always active'}</span>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              {de
                ? 'Diese Cookies sind für die Grundfunktionen der Website erforderlich und können nicht deaktiviert werden. Sie werden in der Regel als Reaktion auf von Ihnen getätigte Aktionen gesetzt, wie z. B. das Festlegen Ihrer Datenschutzeinstellungen oder das Speichern des Warenkorbs.'
                : 'These cookies are required for basic website functionality and cannot be disabled. They are usually set in response to actions you take, such as setting your privacy preferences or saving the shopping cart.'}
            </p>
          </div>

          {/* Statistics */}
          <div className="rounded-xl border border-border p-4">
            <div className="mb-2">
              <span className="text-sm font-semibold text-foreground">{de ? 'Statistik' : 'Statistics'}</span>
              <p className="text-xs text-muted-foreground mt-0.5">
                {de
                  ? 'Diese Cookies ermöglichen es uns, die Nutzung der Website zu analysieren, um deren Leistung zu messen und zu verbessern.'
                  : 'These cookies allow us to analyze website usage in order to measure and improve its performance.'}
              </p>
            </div>
            <div className="mt-3 border-t border-border/50 pt-2">
              <p className="text-xs font-medium text-muted-foreground mb-1">{de ? 'Services' : 'Services'}</p>
              <ServiceRow service={statisticsServices[0]} de={de} checked={ga4} onCheckedChange={setGa4} />
            </div>
          </div>

          {/* Marketing */}
          <div className="rounded-xl border border-border p-4">
            <div className="mb-2">
              <span className="text-sm font-semibold text-foreground">Marketing</span>
              <p className="text-xs text-muted-foreground mt-0.5">
                {de
                  ? 'Diese Technologien werden von Werbetreibenden verwendet, um Anzeigen zu schalten, die für Ihre Interessen relevant sind.'
                  : 'These technologies are used by advertisers to deliver ads that are relevant to your interests.'}
              </p>
            </div>
            <div className="mt-3 border-t border-border/50 pt-2">
              <p className="text-xs font-medium text-muted-foreground mb-1">{de ? 'Services' : 'Services'}</p>
              {marketingServices.map(s => (
                <ServiceRow
                  key={s.id}
                  service={s}
                  de={de}
                  checked={s.id === 'meta-pixel' ? metaPixel : s.id === 'affiliate' ? affiliate : googleAds}
                  onCheckedChange={(v) => s.id === 'meta-pixel' ? setMetaPixel(v) : s.id === 'affiliate' ? setAffiliate(v) : setGoogleAds(v)}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Footer buttons */}
        <div className="p-5 md:p-6 pt-3 border-t border-border flex flex-col gap-2">
          <div className="flex flex-row gap-2">
            <Button size="lg" className="flex-1 bg-[hsl(var(--h2h-black))] text-white hover:opacity-90" onClick={handleNecessaryOnly}>
              {de ? 'Nur notwendige' : 'Necessary only'}
            </Button>
            <Button size="lg" className="flex-1 bg-[hsl(var(--h2h-black))] text-white hover:opacity-90" onClick={handleSaveCustom}>
              {de ? 'Auswahl speichern' : 'Save selection'}
            </Button>
          </div>
          <Button size="lg" className="w-full bg-[hsl(var(--h2h-black))] text-white hover:opacity-90" onClick={handleAcceptAll}>
            {de ? 'Alle akzeptieren' : 'Accept all'}
          </Button>
        </div>
      </div>
    </div>
  );
};
